import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image, Modal, TextInput, Switch, Platform, Alert, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import StatCard from "./StatCard";
import { useData } from "../context/DataContext";
import { useApi } from "../context/ApiContext";

const ProductForm = ({ product, onSave, onCancel }) => {
	const isEdit = !!product;
	const [formData, setFormData] = useState({
		title: product?.title || "",
		description: product?.description || "",
		images: product?.images || [],
		price: product?.price !== undefined && product?.price !== null ? String(product?.price) : "",
		status: product?.status || "draft",
		liveDate: product?.liveDate ? new Date(product.liveDate) : new Date(),
		removalDate: product?.removalDate ? new Date(product.removalDate) : null,
	});

	const [errors, setErrors] = useState({});
	const [imageUrl, setImageUrl] = useState("");

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error when user types
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleStatusToggle = () => {
		setFormData((prev) => ({
			...prev,
			status: prev.status === "active" ? "draft" : "active",
		}));
	};

	const handleAddImage = () => {
		if (!imageUrl.trim()) {
			setErrors((prev) => ({
				...prev,
				imageUrl: "Please enter a valid image URL",
			}));
			return;
		}

		if (!imageUrl.startsWith("https://")) {
			setErrors((prev) => ({
				...prev,
				imageUrl: "Image URL must be a valid HTTPS URL",
			}));
			return;
		}

		setFormData((prev) => ({
			...prev,
			images: [...prev.images, imageUrl],
		}));

		setImageUrl("");
		setErrors((prev) => ({
			...prev,
			imageUrl: null,
		}));
	};

	const handleRemoveImage = (index) => {
		setFormData((prev) => ({
			...prev,
			images: prev.images.filter((_, i) => i !== index),
		}));
	};

	const validateForm = () => {
		const newErrors = {};

		if (!formData.title.trim()) {
			newErrors.title = "Title is required";
		} else if (formData.title.length > 200) {
			newErrors.title = "Title must be less than 200 characters";
		}

		if (formData.price && isNaN(parseFloat(formData.price))) {
			newErrors.price = "Price must be a valid number";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!validateForm()) {
			return;
		}

		const productData = {
			...formData,
			price: formData.price ? parseFloat(formData.price) : null,
		};

		if (isEdit) {
			productData.id = product.id || product._id;
		}

		onSave(productData);
	};

	return (
		<View style={styles.formContainer}>
			{isEdit && (
				<View style={styles.productIdContainer}>
					<Text style={styles.productId}>ID: {product._id || product.id}</Text>
					<View style={[styles.statusBadge, formData.status === "active" ? styles.statusBadgeActive : styles.statusBadgeDraft]}>
						<Text style={styles.statusBadgeText}>{formData.status === "active" ? "ACTIVE" : "DRAFT"}</Text>
					</View>
				</View>
			)}

			<View style={styles.formGroup}>
				<Text style={styles.label}>Title *</Text>
				<TextInput style={[styles.input, errors.title && styles.inputError]} value={formData.title} onChangeText={(text) => handleInputChange("title", text)} placeholder="Product title" placeholderTextColor="#666" />
				{errors.title && <Text style={styles.errorText}>{errors.title}</Text>}
			</View>

			<View style={styles.formGroup}>
				<Text style={styles.label}>Description</Text>
				<TextInput style={[styles.input, styles.textArea]} value={formData.description} onChangeText={(text) => handleInputChange("description", text)} placeholder="Product description" placeholderTextColor="#666" multiline numberOfLines={4} />
			</View>

			<View style={styles.formGroup}>
				<Text style={styles.label}>Price</Text>
				<TextInput style={[styles.input, errors.price && styles.inputError]} value={formData.price} onChangeText={(text) => handleInputChange("price", text)} placeholder="Product price" placeholderTextColor="#666" keyboardType="numeric" />
				{errors.price && <Text style={styles.errorText}>{errors.price}</Text>}
			</View>

			<View style={styles.formGroup}>
				<View style={styles.switchRow}>
					<Text style={styles.label}>Status</Text>
					<View style={styles.switchContainer}>
						<Text style={styles.switchLabel}>{formData.status === "active" ? "Active" : "Draft"}</Text>
						<Switch value={formData.status === "active"} onValueChange={handleStatusToggle} trackColor={{ false: "#444", true: "#0A84FF" }} thumbColor={formData.status === "active" ? "#fff" : "#ddd"} />
					</View>
				</View>
			</View>

			<View style={styles.formGroup}>
				<Text style={styles.label}>Images</Text>
				<View style={styles.imageInputContainer}>
					<TextInput style={[styles.input, styles.imageInput, errors.imageUrl && styles.inputError]} value={imageUrl} onChangeText={setImageUrl} placeholder="Enter image URL" placeholderTextColor="#666" />
					<TouchableOpacity style={styles.addImageButton} onPress={handleAddImage}>
						<Ionicons name="add" size={20} color="#fff" />
					</TouchableOpacity>
				</View>
				{errors.imageUrl && <Text style={styles.errorText}>{errors.imageUrl}</Text>}

				<View style={styles.imagesContainer}>
					{formData.images.length > 0 ? (
						formData.images.map((img, index) => (
							<View key={index} style={styles.imageItem}>
								<Image source={{ uri: img }} style={styles.thumbnailImage} />
								<TouchableOpacity style={styles.removeImageButton} onPress={() => handleRemoveImage(index)}>
									<Ionicons name="close-circle" size={20} color="#ff4040" />
								</TouchableOpacity>
							</View>
						))
					) : (
						<Text style={styles.noImagesText}>No images added</Text>
					)}
				</View>
			</View>

			<View style={styles.formActions}>
				<TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
					<Text style={styles.cancelButtonText}>Cancel</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.saveButton} onPress={handleSubmit}>
					<Text style={styles.saveButtonText}>{isEdit ? "Update Product" : "Create Product"}</Text>
				</TouchableOpacity>
			</View>
		</View>
	);
};

const ProductDialog = ({ visible, onClose, product, onSave }) => {
	const isEdit = !!product;

	return (
		<Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
			<View style={styles.modalOverlay}>
				<View style={styles.modalContent}>
					<View style={styles.modalHeader}>
						<Text style={styles.modalTitle}>{isEdit ? "Edit Product" : "Create Product"}</Text>
						<TouchableOpacity onPress={onClose} style={styles.closeButton}>
							<Ionicons name="close" size={24} color="#fff" />
						</TouchableOpacity>
					</View>

					<ScrollView style={styles.formScrollContainer}>
						<ProductForm product={product} onSave={onSave} onCancel={onClose} />
					</ScrollView>
				</View>
			</View>
		</Modal>
	);
};

const ProductsContent = ({ appId }) => {
	const { productData, refetchData } = useData();
	const { api } = useApi();
	const [dialogVisible, setDialogVisible] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [saving, setSaving] = useState(false);

	const handleAddProduct = () => {
		setSelectedProduct(null);
		setDialogVisible(true);
	};

	const handleEditProduct = (product) => {
		setSelectedProduct(product);
		setDialogVisible(true);
	};

	const handleCloseDialog = () => {
		setDialogVisible(false);
		setSelectedProduct(null);
	};

	const handleSaveProduct = async (productData) => {
		try {
			setSaving(true);

			const requestBody = {
				appId: appId,
				action: selectedProduct ? "update_product" : "create_product",
			};

			if (selectedProduct) {
				requestBody.productId = selectedProduct.id || selectedProduct._id;
				requestBody.updateData = productData;
			} else {
				requestBody.productData = productData;
			}

			// Call the API endpoint
			const response = await api("/app", {
				method: "POST",
				body: requestBody,
			});

			// Refresh the product data
			await refetchData("products");

			// Close dialog immediately after successful save
			handleCloseDialog();

			// Show success message
			Alert.alert("Success", `Product ${selectedProduct ? "updated" : "created"} successfully!`);
		} catch (error) {
			console.error("Error saving product:", error);
			Alert.alert("Error", `Failed to ${selectedProduct ? "update" : "create"} product: ${error.message || "Unknown error"}`);
		} finally {
			setSaving(false);
		}
	};

	return (
		<ScrollView style={styles.fullContent}>
			<View style={styles.contentHeader}>
				<Text style={styles.contentTitle}>Products</Text>
				<TouchableOpacity style={styles.addButton} onPress={handleAddProduct}>
					<Ionicons name="add" size={20} color="#fff" />
					<Text style={styles.addButtonText}>Add Product</Text>
				</TouchableOpacity>
			</View>

			<View style={styles.statsGrid}>
				<StatCard title="Total Products" value={productData?.length || "0"} icon="cube-outline" />
				<StatCard title="Active" value={productData?.filter((p) => p.status === "active").length || "0"} icon="checkmark-circle-outline" />
				<StatCard title="Draft Products" value={productData?.filter((p) => p.status === "draft" || !p.status).length || "0"} icon="document-outline" />
			</View>

			<View style={styles.productsGrid}>
				{productData && productData.length > 0 ? (
					productData.map((product, index) => (
						<TouchableOpacity key={product._id || index} style={styles.productCard} onPress={() => handleEditProduct(product)}>
							{product.images && product.images.length > 0 ? (
								<Image source={{ uri: product.images[0] }} style={styles.productImage} />
							) : (
								<View style={styles.placeholderImage}>
									<Ionicons name="cube-outline" size={24} color="#666" />
								</View>
							)}

							<View style={styles.productCardContent}>
								<Text style={styles.productTitle} numberOfLines={2}>
									{product.title || "Untitled Product"}
								</Text>
								<View style={styles.productCardFooter}>
									<Text style={styles.productPrice}>{product.price ? `$${product.price}` : "FREE"}</Text>
									<View style={[styles.cardStatusBadge, product.status === "active" ? styles.statusBadgeActive : styles.statusBadgeDraft]}>
										<Text style={styles.cardStatusText}>{product.status === "active" ? "Live" : "Draft"}</Text>
									</View>
								</View>
							</View>
						</TouchableOpacity>
					))
				) : (
					<View style={styles.emptyState}>
						<Ionicons name="cube-outline" size={48} color="#666" />
						<Text style={styles.emptyText}>No products yet</Text>
					</View>
				)}
			</View>

			<ProductDialog visible={dialogVisible} onClose={handleCloseDialog} product={selectedProduct} onSave={handleSaveProduct} />

			{saving && (
				<View style={styles.loadingOverlay}>
					<View style={styles.loadingContainer}>
						<ActivityIndicator size="large" color="#0A84FF" />
						<Text style={styles.loadingText}>{selectedProduct ? "Updating" : "Creating"} product...</Text>
					</View>
				</View>
			)}
		</ScrollView>
	);
};

const styles = StyleSheet.create({
	fullContent: {
		flex: 1,
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
	},
	contentHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 24,
	},
	contentTitle: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
	},
	addButton: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		paddingHorizontal: 16,
		height: 40,
		gap: 8,
	},
	addButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	statsGrid: {
		flexDirection: "row",
		gap: 16,
		marginBottom: 20,
	},
	productsGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 12,
		marginTop: 24,
		width: "100%",
	},
	productCard: {
		width: 180,
		height: 180,
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 12,
		overflow: "hidden",
		margin: 6,
		display: "flex",
		flexDirection: "column",
	},
	productImage: {
		width: "100%",
		height: 110,
		borderRadius: 8,
		marginBottom: 8,
	},
	placeholderImage: {
		width: "100%",
		height: 110,
		borderRadius: 8,
		backgroundColor: "#3A3A3A",
		justifyContent: "center",
		alignItems: "center",
		marginBottom: 8,
	},
	productCardContent: {
		flex: 1,
		display: "flex",
		justifyContent: "space-between",
	},
	productCardFooter: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginTop: 4,
	},
	productTitle: {
		color: "#fff",
		fontSize: 13,
		fontWeight: "500",
		marginBottom: 4,
	},
	productPrice: {
		color: "#0A84FF",
		fontSize: 12,
		fontWeight: "600",
	},
	emptyState: {
		width: "100%",
		padding: 40,
		justifyContent: "center",
		alignItems: "center",
	},
	emptyText: {
		color: "#666",
		fontSize: 16,
		marginTop: 12,
	},
	// Modal Styles
	modalOverlay: {
		flex: 1,
		backgroundColor: "rgba(0, 0, 0, 0.7)",
		justifyContent: "center",
		alignItems: "center",
	},
	modalContent: {
		width: "70%",
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		overflow: "hidden",
		maxHeight: "80%",
		display: "flex",
		flexDirection: "column",
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	modalTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
	},
	closeButton: {
		padding: 4,
	},
	formScrollContainer: {
		flexGrow: 1,
	},
	formContainer: {
		paddingHorizontal: 20,
		paddingVertical: 16,
	},
	formGroup: {
		marginBottom: 20,
	},
	label: {
		fontSize: 14,
		fontWeight: "500",
		color: "#fff",
		marginBottom: 8,
	},
	input: {
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
		borderWidth: 1,
		borderColor: "#444",
	},
	inputError: {
		borderColor: "#ff4040",
	},
	errorText: {
		color: "#ff4040",
		fontSize: 12,
		marginTop: 4,
	},
	textArea: {
		minHeight: 100,
		textAlignVertical: "top",
	},
	switchRow: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	switchContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	switchLabel: {
		color: "#fff",
		fontSize: 14,
	},
	imageInputContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	imageInput: {
		flex: 1,
	},
	addImageButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		width: 40,
		height: 40,
		alignItems: "center",
		justifyContent: "center",
	},
	imagesContainer: {
		marginTop: 12,
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 10,
	},
	imageItem: {
		position: "relative",
		width: 80,
		height: 80,
		borderRadius: 8,
		overflow: "hidden",
	},
	thumbnailImage: {
		width: "100%",
		height: "100%",
	},
	removeImageButton: {
		position: "absolute",
		top: 4,
		right: 4,
		backgroundColor: "rgba(0,0,0,0.6)",
		borderRadius: 12,
		width: 24,
		height: 24,
		alignItems: "center",
		justifyContent: "center",
	},
	noImagesText: {
		color: "#666",
		fontSize: 14,
		paddingVertical: 16,
		textAlign: "center",
	},
	formActions: {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 12,
		marginTop: 24,
	},
	cancelButton: {
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderRadius: 6,
		borderWidth: 1,
		borderColor: "#444",
	},
	cancelButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	saveButton: {
		backgroundColor: "#0A84FF",
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderRadius: 6,
	},
	saveButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	productIdContainer: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	productId: {
		fontSize: 12,
		color: "#888",
	},
	statusBadge: {
		paddingHorizontal: 8,
		paddingVertical: 3,
		borderRadius: 4,
	},
	statusBadgeActive: {
		backgroundColor: "#0A84FF",
	},
	statusBadgeDraft: {
		backgroundColor: "#555",
	},
	statusBadgeText: {
		color: "#fff",
		fontSize: 10,
		fontWeight: "bold",
	},
	loadingOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.7)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1000,
	},
	loadingContainer: {
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 24,
		alignItems: "center",
		justifyContent: "center",
		minWidth: 200,
	},
	loadingText: {
		color: "#fff",
		fontSize: 16,
		marginTop: 16,
	},
	cardStatusBadge: {
		paddingHorizontal: 6,
		paddingVertical: 2,
		borderRadius: 4,
		minWidth: 40,
		alignItems: "center",
	},
	cardStatusText: {
		color: "#fff",
		fontSize: 9,
		fontWeight: "bold",
	},
});

export default ProductsContent;
